/**
 * Journal anonyme d'une séance, et son analyse.
 *
 * Règle non négociable n° 5 : **rien d'identifiant n'entre ici**. Une entrée
 * porte un instant, un type, un chapitre, une étape, et parfois l'identifiant
 * d'un problème ou d'un statut de chaîne — tous venus du contenu. Jamais un
 * texte écrit par un élève.
 *
 * Le moteur ne date rien lui-même : il est pur et déterministe. C'est
 * l'appelant qui fournit l'horodatage, ce qui laisse les tests maîtres du temps.
 */
import type { ChapterId, Content } from './content-schema.js';

export const JOURNAL_EVENT_TYPES = [
  'etape_debut',
  'etape_fin',
  'chaine_essai',
  'erreur_classement',
  'retour_arriere',
] as const;

export type JournalEventType = (typeof JOURNAL_EVENT_TYPES)[number];

export interface JournalEntry {
  /** Instant, en millisecondes depuis l'époque. */
  readonly t: number;
  readonly type: JournalEventType;
  readonly chapitre: ChapterId;
  /** Indice de l'étape concernée. */
  readonly etape: number;
  /**
   * Précision venue du contenu : identifiant de problème mal classé, statut
   * d'une chaîne essayée. Jamais un texte libre.
   */
  readonly detail: string | null;
}

/**
 * Taille maximale du journal d'un îlot.
 *
 * Une séance de cinquante minutes en produit quelques dizaines. Le plafond
 * protège la limite de 256 Ko d'une sauvegarde contre une boucle imprévue : au
 * delà, les entrées les plus anciennes sortent.
 */
export const MAX_ENTREES_JOURNAL = 500;

/** Ajoute une entrée, en gardant le journal sous son plafond. */
export function ajouterAuJournal(
  journal: readonly JournalEntry[],
  entree: JournalEntry,
): readonly JournalEntry[] {
  const suivant = [...journal, entree];
  return suivant.length > MAX_ENTREES_JOURNAL
    ? suivant.slice(suivant.length - MAX_ENTREES_JOURNAL)
    : suivant;
}

/* ------------------------------------------------------------------ */
/* Analyse de séance                                                   */
/* ------------------------------------------------------------------ */

export interface AnalyseDEtape {
  readonly chapitre: ChapterId;
  readonly etape: number;
  readonly titre: string;
  /** Temps médian passé sur l'étape, en secondes. `null` si personne ne l'a finie. */
  readonly tempsMedianSecondes: number | null;
  /** Nombre d'îlots ayant commencé l'étape. */
  readonly ilotsArrives: number;
  /** Nombre d'îlots l'ayant terminée. */
  readonly ilotsTermines: number;
  /** Îlots arrivés mais jamais repartis : c'est là que ça bloque. */
  readonly ilotsBloques: number;
}

export interface ErreurFrequente {
  readonly type: JournalEventType;
  readonly detail: string;
  readonly libelle: string;
  readonly occurrences: number;
  /** Nombre d'îlots distincts concernés : une erreur partagée n'est pas la même chose qu'une erreur répétée. */
  readonly ilots: number;
}

export interface AnalyseDeSeance {
  readonly ilots: number;
  readonly etapes: readonly AnalyseDEtape[];
  /** Les étapes qui bloquent le plus, les plus coûteuses en premier. */
  readonly blocages: readonly AnalyseDEtape[];
  /** Les erreurs les plus fréquentes, les plus nombreuses en premier. */
  readonly erreurs: readonly ErreurFrequente[];
}

/** Médiane d'une liste de nombres. */
export function mediane(valeurs: readonly number[]): number | null {
  if (valeurs.length === 0) {
    return null;
  }
  const triees = [...valeurs].sort((a, b) => a - b);
  const milieu = Math.floor(triees.length / 2);

  return triees.length % 2 === 1
    ? (triees[milieu] as number)
    : ((triees[milieu - 1] as number) + (triees[milieu] as number)) / 2;
}

const cle = (chapitre: string, etape: number): string => `${chapitre}#${etape}`;

/** Le titre d'une étape, tel qu'il est écrit dans le contenu. */
function titreDEtape(content: Content, chapitre: string, etape: number): string {
  return content.chapitres.find((c) => c.id === chapitre)?.deroule[etape]?.titre ?? `${etape + 1}`;
}

/** Un libellé lisible pour une erreur : titre du problème, ou statut de chaîne. */
function libelleDErreur(content: Content, type: JournalEventType, detail: string): string {
  if (type === 'erreur_classement') {
    return content.problemes.find((p) => p.id === detail)?.titre ?? detail;
  }
  return detail;
}

/**
 * Agrège les journaux de plusieurs îlots en une vue de séance.
 *
 * Les journaux arrivent tels que les îlots les ont écrits : un îlot qui a
 * quitté sa tablette au milieu d'une étape laisse un début sans fin, et c'est
 * précisément ce qu'on veut voir.
 */
export function analyserSeance(
  journaux: readonly (readonly JournalEntry[])[],
  content: Content,
): AnalyseDeSeance {
  const durees = new Map<string, number[]>();
  const arrives = new Map<string, Set<number>>();
  const termines = new Map<string, Set<number>>();
  const erreurs = new Map<
    string,
    { type: JournalEventType; detail: string; occurrences: number; ilots: Set<number> }
  >();

  journaux.forEach((journal, indexIlot) => {
    /** Dernier début d'étape non encore refermé, par étape. */
    const debuts = new Map<string, number>();

    for (const entree of journal) {
      const identifiant = cle(entree.chapitre, entree.etape);

      switch (entree.type) {
        case 'etape_debut':
          debuts.set(identifiant, entree.t);
          (arrives.get(identifiant) ?? arrives.set(identifiant, new Set()).get(identifiant)!).add(
            indexIlot,
          );
          break;

        case 'etape_fin': {
          const debut = debuts.get(identifiant);
          if (debut !== undefined && entree.t >= debut) {
            (durees.get(identifiant) ?? durees.set(identifiant, []).get(identifiant)!).push(
              (entree.t - debut) / 1000,
            );
            debuts.delete(identifiant);
          }
          (termines.get(identifiant) ?? termines.set(identifiant, new Set()).get(identifiant)!).add(
            indexIlot,
          );
          break;
        }

        case 'erreur_classement':
        case 'chaine_essai': {
          if (entree.detail === null) break;
          const identifiantErreur = `${entree.type}#${entree.detail}`;
          const existante =
            erreurs.get(identifiantErreur) ??
            erreurs
              .set(identifiantErreur, {
                type: entree.type,
                detail: entree.detail,
                occurrences: 0,
                ilots: new Set(),
              })
              .get(identifiantErreur)!;
          existante.occurrences += 1;
          existante.ilots.add(indexIlot);
          break;
        }

        default:
          break;
      }
    }
  });

  const etapes: AnalyseDEtape[] = [];
  for (const chapitre of content.chapitres) {
    chapitre.deroule.forEach((_, etape) => {
      const identifiant = cle(chapitre.id, etape);
      const nombreArrives = arrives.get(identifiant)?.size ?? 0;
      const nombreTermines = termines.get(identifiant)?.size ?? 0;

      etapes.push({
        chapitre: chapitre.id,
        etape,
        titre: titreDEtape(content, chapitre.id, etape),
        tempsMedianSecondes: mediane(durees.get(identifiant) ?? []),
        ilotsArrives: nombreArrives,
        ilotsTermines: nombreTermines,
        ilotsBloques: Math.max(0, nombreArrives - nombreTermines),
      });
    });
  }

  return {
    ilots: journaux.length,
    etapes,
    blocages: [...etapes]
      .filter((etape) => etape.ilotsBloques > 0)
      .sort((a, b) => b.ilotsBloques - a.ilotsBloques),
    erreurs: [...erreurs.values()]
      .map((erreur) => ({
        type: erreur.type,
        detail: erreur.detail,
        libelle: libelleDErreur(content, erreur.type, erreur.detail),
        occurrences: erreur.occurrences,
        ilots: erreur.ilots.size,
      }))
      .sort((a, b) => b.occurrences - a.occurrences),
  };
}
